import { ANALYTICS_EVENTS, ORDER_TYPES, ORDER_TYPES_LABELS } from '@nandosaus/constants/lib/constants';
import { useRootStore } from '@nandosaus/state-management';
import { isString, some } from 'lodash';
import { observer } from 'mobx-react';
import { PropTypes } from 'prop-types';
import { useEffect, useState } from 'react';

import { OrderSettingsModalHeader } from '@components/order-settings-modal/order-settings-modal-header';
import { RestaurantFinderScreen } from '@components/order-settings-modal/restaurant-finder-screen';
import { DeliveryAddressModal } from '@components/delivery-address-modal';
import { analytics } from '../../analytics';
import { useGoogleMaps } from '../../hooks/use-google-maps';
import { Button } from '../button/new';
import { ErrorBoundary } from '../error-boundary';
import { Modal } from '../modal/new';
import { OrderSettingsScreen } from './order-settings-screen';

const MODAL_SCREENS = {
  DEFAULT: 'default',
  RESTAURANT_FINDER: 'finder',
  DELIVERY_ADDRESS: 'deliveryAddress',
};

const OrderSettingsModal = ({ onClose }) => {
  const [modalScreen, setModalScreen] = useState(MODAL_SCREENS.DEFAULT);
  const { OrderingContextStore, RestaurantStore, DeliveryStore } = useRootStore();

  // On a fresh /catering load with no preset restaurant, nothing else triggers
  // the restaurant fetch before this modal auto-opens (InPageNav's
  // OrderSettingsSummary only mounts once restaurants are already loaded). Guard
  // the load here so the finder never searches an empty store and falsely
  // reports "no restaurants in 200km". Mirrors checkout / OrderSettingsSummary.
  useEffect(() => {
    if (!RestaurantStore.loaded && !RestaurantStore.loading) {
      RestaurantStore.loadRestaurants();
    }
  }, [RestaurantStore]);

  const [settings, setSettings] = useState({
    // Preserve a previously-chosen Delivery selection when reopening the modal.
    orderType: OrderingContextStore.isCateringDelivery ? ORDER_TYPES.CATERING_DELIVERY : ORDER_TYPES.CATERING,
    restaurant: OrderingContextStore.restaurant,
    cateringPickupDate: OrderingContextStore.cateringPickupDate,
    cateringPickupTime: OrderingContextStore.cateringPickupTime,
    cateringHeadcount: OrderingContextStore.cateringHeadcount,
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Preload Google Maps library
  useGoogleMaps();
  /*
    Update a given setting in local state and revalidate the changed field
    to see if any additional errors. If changing order type then all
    settings are revalidated to ensure old errors from no-longer relevant
    fields pollute the errors.
  */
  const updateSetting = (key, value) => {
    const newSettings = {
      ...settings,
      [key]: value,
    };

    setSettings(newSettings);

    // Commit the order type to the live store immediately, mirroring how CATERING is
    // committed at page load. Deferring this to final confirm creates a chicken-and-egg
    // bug for CATERING_DELIVERY: the delivery estimate (fetched while entering the address,
    // before confirm) reads isCateringDelivery off the store, so it must already be true.
    if (key === 'orderType') {
      OrderingContextStore.setOrderType(value);
    }

    // Commit schedule fields live too, for the same reason as orderType above: the
    // delivery-address flow fetches a fresh Uber quote as soon as the address changes,
    // reading cateringPickupDateTime off the live store. Deferring these to Save and
    // Continue means picking a new date/time and then changing the address would quote
    // against the stale, previously-committed time. cateringPickupDateTime needs both
    // fields, so commit both together off newSettings rather than just the changed key.
    if (key === 'cateringPickupDate' || key === 'cateringPickupTime') {
      OrderingContextStore.updateSettings({
        cateringPickupDate: newSettings.cateringPickupDate,
        cateringPickupTime: newSettings.cateringPickupTime,
      });

      // The Uber quote is otherwise only re-fetched when the address changes
      // (DeliveryStore.setDeliveryAddress) or cart contents change — nothing
      // previously re-fetched it when just the date/time changed, so it went
      // stale until the address was touched again. getDeliveryQuote() is a
      // safe no-op without a delivery address.
      if (OrderingContextStore.isCateringDelivery) {
        DeliveryStore.getDeliveryQuote();
      }
    }

    const errors = OrderingContextStore.validateSettings(newSettings);

    if (key === 'orderType') {
      setValidationErrors(errors);
    } else {
      setValidationErrors({
        ...validationErrors,
        [key]: errors[key],
      });
    }
  };

  const handleDismiss = () => {
    analytics.track(ANALYTICS_EVENTS.ORDER_SETTINGS_DISMISSED);
    onClose();
  };

  const handleConfirm = () => {
    const { errors, success } = OrderingContextStore.validateThenUpdateSettings(settings);

    setValidationErrors(errors);

    if (success) {
      analytics.track(ANALYTICS_EVENTS.ORDER_SETTINGS_UPDATED, {
        orderType: ORDER_TYPES_LABELS[OrderingContextStore.orderType],
        restaurant: settings.restaurant?.name,
        pickupDate: settings.pickupDate,
      });

      onClose();
    }
  };

  if (modalScreen === MODAL_SCREENS.RESTAURANT_FINDER) {
    return (
      <Modal
        header={
          <OrderSettingsModalHeader
            title="Find a Nando's"
            handleBack={() => setModalScreen(MODAL_SCREENS.DEFAULT)}
            handleClose={handleDismiss}
          />
        }
        handleClose={handleDismiss}
        variant="side"
        open
      >
        <RestaurantFinderScreen
          orderType={settings.orderType}
          onSelect={newRestaurant => {
            updateSetting('restaurant', newRestaurant);
            setModalScreen(MODAL_SCREENS.DEFAULT);
          }}
        />
      </Modal>
    );
  }

  if (modalScreen === MODAL_SCREENS.DELIVERY_ADDRESS) {
    return (
      <DeliveryAddressModal
        orderType={ORDER_TYPES.CATERING_DELIVERY}
        onCancel={() => setModalScreen(MODAL_SCREENS.DEFAULT)}
        onClose={handleDismiss}
        onConfirm={() => {
          setModalScreen(MODAL_SCREENS.DEFAULT);
          setValidationErrors(OrderingContextStore.validateSettings(settings, true));
        }}
        renderHeader={({ handleBack, handleClose, title }) => (
          <OrderSettingsModalHeader title={title} handleBack={handleBack} handleClose={handleClose} />
        )}
      />
    );
  }

  return (
    <Modal
      buttons={{
        primary: (
          <Button
            disabled={some(validationErrors, isString)}
            onClick={handleConfirm}
            size="tall"
            isFullWidth
            widthType="fullWidth"
          >
            Save and Continue
          </Button>
        ),
      }}
      headingHeader
      handleClose={handleDismiss}
      hideBottomDecal={false}
      hasFooterDivider
      modalTitle={`Catering Order \n Details`}
      variant="side"
      open
    >
      <ErrorBoundary>
        <OrderSettingsScreen
          orderType={settings.orderType}
          restaurant={settings.restaurant}
          showRestaurantFinder={() => setModalScreen(MODAL_SCREENS.RESTAURANT_FINDER)}
          showDeliveryAddressFinder={() => setModalScreen(MODAL_SCREENS.DELIVERY_ADDRESS)}
          updateSetting={updateSetting}
          settings={settings}
          validationErrors={validationErrors}
        />
      </ErrorBoundary>
    </Modal>
  );
};

OrderSettingsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

const ComposedOrderSettingModal = observer(OrderSettingsModal);
export { ComposedOrderSettingModal as OrderSettingsModalCatering };
